/** Motion helpers shared by every GSAP entrypoint. Two pure functions
 *  that pair with the global `prefers-reduced-motion` CSS rule added
 *  in P5/C — components that drive JS animations check this before
 *  registering any tweens / scroll triggers. */

/** True when the user has asked the OS for reduced motion. Safe to
 *  call in SSR — returns false off the browser. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Defer a callback until the browser is idle (~50ms of nothing-going-on).
 *  Falls back to a short setTimeout in browsers without
 *  requestIdleCallback (Safari today). The intent is to keep below-fold
 *  animation setup off the LCP critical path. */
export function runWhenIdle(fn: () => void, fallbackMs = 16): void {
  if (typeof window === 'undefined') { fn(); return; }
  const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(fn);
  } else {
    setTimeout(fn, fallbackMs);
  }
}

/**
 * True when entrance animations should not run at all: the user asked for
 * reduced motion, or the page is loading in a background tab.
 *
 * The background-tab case is the one that bites. `gsap.from()` sets its start
 * state (often `opacity: 0`) the moment it is created and relies on the tween
 * running to undo it. A hidden tab throttles requestAnimationFrame, so the
 * tween stalls mid-stagger and the content is stranded invisible — which is
 * exactly what a guest sees after cmd-clicking several listings to compare
 * them. Skipping the animation leaves the content visible, which is the state
 * that matters.
 */
export function skipEntranceAnimation(): boolean {
  if (prefersReducedMotion()) return true;
  if (typeof document === 'undefined') return false;
  return document.visibilityState === 'hidden';
}

/**
 * Safety net for an entrance tween that may never finish. Clears the inline
 * styles GSAP left behind after `ms`, so a stalled or interrupted tween can't
 * leave content invisible or offset.
 *
 * Pass the same targets and props the tween animates. Returns a cancel
 * function for ngOnDestroy.
 */
export function clearAnimationPropsAfter(
  gsapInstance: { set: (targets: unknown, vars: Record<string, unknown>) => unknown },
  targets: string | Element | Element[],
  ms = 3000,
  props = 'opacity,transform',
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const timer = setTimeout(() => {
    try {
      gsapInstance.set(targets, { clearProps: props });
    } catch {
      /* targets may be gone by now — nothing to clear */
    }
  }, ms);
  return () => clearTimeout(timer);
}
